import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '@material-ui/core/Input';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import Paper from '~/components/Paper';
import Chip from '~/components/Chip';
import InputControl from '~/components/Input/InputControl';
import Form from '~/containers/Form';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import Request from '~/providers/Request';
import useStyles from './styles';

const PATH = '/admin/whatsapp_campaign/settings';
const REQUEST_CONFIG = { controller: 'infobipAccess' };

const toAccess = ({ sender, has_api_token, env_api_token }) => ({
  sender: sender || '',
  api_token: '',
  has_api_token: Boolean(has_api_token),
  env_api_token: Boolean(env_api_token),
});

function InfobipAccess() {
  const classes = useStyles();
  const { t } = useTranslation();
  const tr = (key, options) => t(`infobipAccess.extras.${key}`, options);
  const trAttr = (attr) => t(`infobipAccess.attributes.${attr}`);

  const [access, setAccess] = useState(toAccess({}));
  const [loading, setLoading] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);
  const [confirmRemoveToken, setConfirmRemoveToken] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Request.get(PATH, {});
      setAccess(toAccess(response.data));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (attrs) => setAccess({ ...access, ...attrs });

  async function save() {
    setConfirmSave(false);
    const attributes = { sender: access.sender };
    const token = `${access.api_token || ''}`.trim();
    if (token) attributes.api_token = token;
    try {
      const response = await Request.put(PATH, { data: { attributes } }, {}, REQUEST_CONFIG);
      setAccess(toAccess(response.data));
      toast.success(tr('saved'));
    } catch (e) {
      console.error(e);
    }
  }

  async function removeToken() {
    setConfirmRemoveToken(false);
    try {
      const response = await Request.put(PATH, { data: { attributes: { remove_api_token: true } } }, {}, REQUEST_CONFIG);
      setAccess(toAccess(response.data));
      toast.success(tr('tokenRemoved'));
    } catch (e) {
      console.error(e);
    }
  }

  const hasToken = access.has_api_token;

  const tokenChip = hasToken
    ? <Chip color="green" label={tr('tokenConfigured')} />
    : access.env_api_token
      ? <Chip color="orange" label={tr('tokenMissing')} />
      : <Chip color="red" label={tr('tokenNone')} />;

  return (
    <>
      <Form onSubmit={async () => setConfirmSave(true)} isPersisted showCancel={false}>
        <Paper
          Icon={VpnKeyIcon}
          iconColor="orange"
          title={tr('title')}
          loading={loading}
          actions={<div className={classes.paperActions}>{tokenChip}</div>}
        >
          <div className={classes.section}>
            <GridContainer>
              <GridItem xs={12} md={4}>
                <InputControl
                  name="sender"
                  value={access.sender}
                  label={trAttr('sender')}
                  required
                  inputComponent={Input}
                  onChange={handleChange}
                  InputProps={{ inputProps: { inputMode: 'numeric', maxLength: 15 } }}
                />
                <Typography className={classes.help}>{tr('senderHelp')}</Typography>
              </GridItem>
              <GridItem xs={12} md={6}>
                <InputControl
                  name="api_token"
                  value={access.api_token}
                  label={trAttr('api_token')}
                  inputComponent={Input}
                  onChange={handleChange}
                  InputProps={{
                    type: 'password',
                    placeholder: hasToken ? '••••••••••••••••••••••••' : '',
                    inputProps: { autoComplete: 'new-password' },
                  }}
                />
                {hasToken && (
                  <Button size="small" className={classes.smallButton} onClick={() => setConfirmRemoveToken(true)}>
                    {tr('removeToken')}
                  </Button>
                )}
              </GridItem>
            </GridContainer>
          </div>
        </Paper>
      </Form>

      <Dialog open={confirmSave} onClose={() => setConfirmSave(false)}>
        <DialogTitle>{tr('saveTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{tr('saveConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSave(false)}>{t('form-cancel')}</Button>
          <Button color="primary" variant="contained" onClick={save}>{tr('confirm')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmRemoveToken} onClose={() => setConfirmRemoveToken(false)}>
        <DialogTitle>{tr('removeToken')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{tr('removeTokenConfirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemoveToken(false)}>{t('form-cancel')}</Button>
          <Button color="primary" variant="contained" onClick={removeToken}>{tr('confirm')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default InfobipAccess;
